import MobileOfferOperationModal from '@c/dashboard/OfferOperation/MobileOfferOperationModal';
import OfferOperationModal from '@c/dashboard/OfferOperation/OfferOperationModal';
import { SoldOverlay } from '@c/dashboard/pages/ListingsPage';
import { StatusBadgeNew } from '@c/dashboard/StatusBadgeNew';
import { CopyIcon } from '@c/icons';
import { ChevronDown, ChevronRight, ChevronUp } from '@c/icons/chevron';
import BuyAddOfferButtons from '@c/product-details/BuyAddOfferButtons';
import { useQuery } from '@tanstack/react-query';
import Chip from '@ui/Chip';
import ProductPriceRange from '@ui/ProductPriceRange';
import SafeImage from '@ui/SafeImage';
import {
  getOffer,
  getOfferByBuyer,
  getOfferBySeller,
} from '@util/firestore/offers/offers.service';
import { getProductsByIds } from '@util/firestore/products';
import {
  cn,
  getOfferStatusBadge,
  isMobile,
  offerStateToText,
} from '@util/index';
import { useSellerPayoutSchedules } from '@util/hooks/useSellerPayoutSchedules';
import { useToastContext } from 'context/ToastContext';
import { motion, Variants } from 'framer-motion';
import type { ChatDocument } from 'models/chat';
import type { OfferDocument } from 'models/offer';
import {
  getOrderStatus,
  OrderStatesEnum,
  type OrderDocument,
} from 'models/order';
import type { ProductDocument } from 'models/product';
import type { UserDocument } from 'models/user';
import Link from 'next/link';
import { useState } from 'react';

export type ChatPreviewSidebarProps = {
  product: ProductDocument | null | undefined;
  offer: OfferDocument | null | undefined;
  order: OrderDocument | null | undefined;
  userDoc: UserDocument | null | undefined;
  isFirstTimeLoading?: boolean;
  chat: ChatDocument;
  isLoading: boolean;
};

export default function ChatPreviewSidebar({
  product,
  offer,
  order,
  userDoc,
  isFirstTimeLoading = false,
  chat,
  isLoading,
}: ChatPreviewSidebarProps) {
  const [openModal, setOpenModal] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', order?.product_ids],
    queryFn: () => getProductsByIds(order?.product_ids ?? []),
    enabled: !!order?.product_ids,
  });

  const { data: orderOffer } = useQuery({
    queryKey: ['offer', order?.offer_id, userDoc?.uid],
    queryFn: () => {
      if (!order || !order.offer_id) return null;
      if (userDoc?.roles?.support) {
        return getOffer(order.offer_id);
      }
      const isBuyer = order.buyer_id === userDoc?.uid;
      const isSeller = order.seller_arr?.includes(userDoc?.uid ?? '') ?? false;
      if (isBuyer) {
        return getOfferByBuyer({
          buyer_id: userDoc?.uid ?? '',
          offer_id: order.offer_id,
        });
      }
      if (isSeller) {
        return getOfferBySeller({
          seller_id: userDoc?.uid ?? '',
          offer_id: order.offer_id,
        });
      }
      return null;
    },
    enabled: !!order?.offer_id && !!order?.is_offer,
  });

  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;

  const getChipColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'offer-accepted';
      case 'declined':
        return 'offer-declined';
      case 'pending':
        return 'offer-pending';
      case 'expired':
        return 'offer-expired';
      default:
        return 'gray';
    }
  };

  const orderStatusInfo = getOrderStatus(order, userDoc);
  const payoutProductIds =
    order && userDoc && order.seller_arr?.includes(userDoc.uid)
      ? (order.sellers?.[userDoc.uid] ?? [])
          .filter((item) => item.state === OrderStatesEnum.Delivered)
          .map((item) => item.id)
      : [];
  const { data: payoutSchedules, isLoading: payoutScheduleLoading } =
    useSellerPayoutSchedules({
      orderId: order?.id,
      productIds: payoutProductIds,
      enabled: !!order?.payment_id,
    });
  const nextPayoutSchedule = payoutSchedules
    ?.filter((schedule) => schedule.scheduled_at)
    .sort((a, b) => a.scheduled_at! - b.scheduled_at!)[0];

  // if logged in as support and the order has more than 1 product, don't show the order status
  const shouldShowOrderStatus = !(
    userDoc?.roles?.admin || userDoc?.roles?.support
  )
    ? true
    : (order?.product_ids?.length ?? 0) > 1
    ? false
    : true;

  if (!product && !offer && !order) return;

  const sidebarVariants: Variants = {
    hidden: {
      width: isFirstTimeLoading ? 0 : '40rem',
      opacity: isFirstTimeLoading ? 0 : 1,
    },
    visible: {
      width: '40rem',
      opacity: 1,
      transition: isFirstTimeLoading
        ? {
            type: 'spring',
            stiffness: 200,
            damping: 25,
            duration: 0.8,
            opacity: {
              delay: 0.3,
              duration: 0.5,
            },
          }
        : {
            duration: 0,
          },
    },
  };

  if (order) {
    // Determine which products to show based on user role
    const isBuyer = order.buyer_id === userDoc?.uid;
    const isSeller = order.seller_arr?.includes(userDoc?.uid ?? '') ?? false;
    const displayedProducts = isLoggedInAsSupport
      ? products // Show all products for support/admin
      : isBuyer
      ? products // Show all purchased products for buyers
      : isSeller
      ? products?.filter((product) => product.seller_id === userDoc?.uid) // Show only seller's products
      : [];

    // Get the correct order link based on user role
    const getOrderHref = () => {
      if (userDoc && (isBuyer || isLoggedInAsSupport)) {
        return `/dashboard/purchases/${order.id}`;
      }
      return `/dashboard/orders/${order.id}`;
    };

    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="flex w-full flex-col gap-6">
          {isLoading ? (
            <div className="flex w-full items-center gap-4">
              <div className="h-[2.1rem] w-48 animate-pulse rounded bg-zinc-200" />
              <div className="h-10 w-10 animate-pulse rounded bg-zinc-200" />
            </div>
          ) : (
            <Link
              href={getOrderHref()}
              target="_blank"
              className="group flex w-full items-center gap-4"
            >
              <h3 className="text-[2.1rem] font-semibold text-zinc-700">
                Order #{order.order_num}
              </h3>

              <ChevronRight className="h-10 w-10 text-zinc-500 transition-all ease-linear group-hover:ml-2" />
            </Link>
          )}

          {displayedProducts && displayedProducts.length > 0 && (
            <div className="flex flex-col gap-4 border-b border-zinc-200 pb-6">
              {isLoading || isLoadingProducts ? (
                <div className="flex flex-col gap-12">
                  {/* Skeleton for 2 products */}
                  {[1, 2].map((index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="aspect-square h-[10rem] w-[10rem] animate-pulse rounded-2xl bg-zinc-200" />
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1">
                          <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                          <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                        </div>
                        <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                        <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-12">
                    {(isExpanded
                      ? displayedProducts
                      : displayedProducts.slice(0, 2)
                    ).map((product, index) => {
                      // Find the order item to get variation info
                      const orderItem = Object.values(order.sellers ?? {})
                        .flat()
                        .find((item) => item.id === product.id);

                      // Get the correct image based on variations
                      const image =
                        orderItem && product.variations
                          ? product.variations.find(
                              (v) =>
                                (!orderItem.variations?.letter ||
                                  v.size === orderItem.variations?.letter) &&
                                (!orderItem.variations?.number ||
                                  v.size === orderItem.variations?.number) &&
                                (!orderItem.variations?.color ||
                                  v.color === orderItem.variations.color)
                            )?.image ?? product.thumbnail
                          : product.thumbnail;

                      return (
                        <div
                          key={product.id || index}
                          className="flex items-center gap-4"
                        >
                          {image ? (
                            <Link href={getOrderHref()} target="_blank">
                              <div className="aspect-square h-[10rem] w-[10rem] overflow-hidden rounded-2xl bg-white transition-opacity hover:opacity-80">
                                <SafeImage
                                  src={image}
                                  alt={product.title ?? ''}
                                  width={400}
                                  key={image}
                                  height={400}
                                  className="aspect-square h-full w-full rounded-2xl object-contain"
                                />
                              </div>
                            </Link>
                          ) : (
                            <div className="aspect-square h-[10rem] w-[10rem] rounded-2xl bg-zinc-100" />
                          )}

                          <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                              <Link
                                href={getOrderHref()}
                                target="_blank"
                                className="group"
                              >
                                <h3 className="line-clamp-2 text-[1.8rem] font-medium text-zinc-700 transition-all ease-linear group-hover:text-zinc-900 group-hover:underline">
                                  {product.title}
                                </h3>
                              </Link>

                              <ProductReferences product={product} />

                              <ProductPriceRange product={product} vertical />
                            </div>

                            <ProductCategoryDetails product={product} />

                            {order.is_offer && orderOffer && (
                              <div className="mt-4">
                                <Chip
                                  text={getOfferStatusBadge(orderOffer)}
                                  color={getChipColor(
                                    offerStateToText(
                                      orderOffer.state
                                    ).toLowerCase()
                                  )}
                                  className="!h-[2.4rem] !w-fit !rounded-lg !text-[1.4rem] !font-semibold"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {displayedProducts.length > 2 && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="mt-4 flex items-center justify-center gap-2 text-[1.6rem] font-medium text-zinc-600 transition-colors hover:text-zinc-900"
                    >
                      <span>{isExpanded ? 'view less' : 'view more'}</span>
                      {isExpanded ? (
                        <ChevronUp width="12" height="12" />
                      ) : (
                        <ChevronDown width="12" height="12" />
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-16">
            {isLoading ? (
              <div className="flex flex-col gap-2">
                <div className="h-[2.1rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-24 animate-pulse rounded bg-zinc-200" />
              </div>
            ) : orderStatusInfo ? (
              <>
                {shouldShowOrderStatus && (
                  <StatusBadgeNew
                    status={orderStatusInfo.status}
                    is_partial={orderStatusInfo.is_partial}
                    variant={orderStatusInfo.variant}
                    date={orderStatusInfo.date}
                    payoutSchedule={nextPayoutSchedule}
                    payoutScheduleLoading={payoutScheduleLoading}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <h4 className="text-[2.1rem] font-semibold text-zinc-700">
                  Order Status
                </h4>
                <span className="text-[1.8rem] font-semibold text-zinc-500">
                  Loading...
                </span>
              </div>
            )}

            {isLoading ? (
              <div className="duration-50 flex h-[5.5rem] animate-pulse rounded-[1.5rem] bg-zinc-200" />
            ) : (
              <Link
                href={getOrderHref()}
                target="_blank"
                className="duration-50 text-zinc-black flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
              >
                View Order
              </Link>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  if (offer && product) {
    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="space-y-6">
          {isLoading ? (
            <div className="aspect-square w-full animate-pulse rounded-2xl bg-zinc-200" />
          ) : (
            <SidebarProductImage product={product} />
          )}

          {isLoading ? (
            <div className="flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6">
              <div className="flex flex-col items-start gap-2">
                <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
              </div>
              <div className="h-10 w-10 animate-pulse rounded-full bg-zinc-200" />
            </div>
          ) : (
            <div className="flex w-full flex-col gap-4 border-b border-zinc-200 pb-6">
              <Link
                className="group flex w-full items-center justify-between gap-6"
                href={`/product/${product.slug}`}
                target="_blank"
              >
                <div className="flex w-full flex-col items-start gap-2">
                  <h3 className="line-clamp-2 text-left text-[1.8rem] font-semibold text-zinc-700">
                    {product.title}
                  </h3>

                  <ProductPriceRange product={product} vertical />

                  {!product.is_auction && (
                    <ProductCategoryDetails product={product} offer={offer} />
                  )}
                </div>

                <ChevronRight className="duration-50 h-10 w-10 rounded-full p-1 text-zinc-500 transition-colors ease-linear group-hover:bg-zinc-100" />
              </Link>

              <ProductReferences product={product} />
            </div>
          )}
        </div>

        {(chat.seller_id === userDoc?.uid || isLoggedInAsSupport) && (
          <Link
            className="duration-50 text-zinc-black mt-8 flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
            target="_blank"
            href={`/product/${product.slug}`}
          >
            View Listing
          </Link>
        )}

        {isMobile() ? (
          <>
            <MobileOfferOperationModal
              show={openModal === 'accept'}
              dismiss={() => setOpenModal('')}
              type="accept"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={openModal === 'counter'}
              dismiss={() => setOpenModal('')}
              type="counter"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={openModal === 'decline'}
              dismiss={() => setOpenModal('')}
              type="decline"
              offer={offer}
            />
          </>
        ) : (
          <>
            <OfferOperationModal
              isOpen={openModal === 'accept'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'accept' : '')}
              type="accept"
              onClose={() => setOpenModal('')}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={openModal === 'counter'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'counter' : '')}
              type="counter"
              onClose={() => setOpenModal('')}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={openModal === 'decline'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'decline' : '')}
              type="decline"
              onClose={() => setOpenModal('')}
              offer={offer}
            />
          </>
        )}
      </motion.div>
    );
  }

  if (product) {
    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="space-y-6">
          {isLoading ? (
            <div className="aspect-square w-full animate-pulse rounded-2xl bg-zinc-200" />
          ) : (
            <SidebarProductImage product={product} />
          )}

          {isLoading ? (
            <div className="flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6">
              <div className="flex flex-col items-start gap-2">
                <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
              </div>
              <div className="h-10 w-10 animate-pulse rounded-full bg-zinc-200" />
            </div>
          ) : (
            <div className="flex w-full flex-col gap-4 border-b border-zinc-200 pb-6">
              <Link
                href={`/product/${product.slug}`}
                target="_blank"
                className="group flex w-full items-center justify-between gap-6"
              >
                <div className="flex flex-col items-start gap-2">
                  <h3 className="line-clamp-2 text-left text-[1.8rem] font-semibold text-zinc-700">
                    {product.title}
                  </h3>

                  <ProductPriceRange product={product} vertical />

                  {!product.is_auction && (
                    <ProductCategoryDetails product={product} />
                  )}
                </div>

                <ChevronRight className="duration-50 h-10 w-10 rounded-full p-1 text-zinc-500 transition-colors ease-linear group-hover:bg-zinc-100" />
              </Link>

              <ProductReferences product={product} />
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col gap-6">
              <div className="duration-50 flex h-[5.5rem] animate-pulse rounded-[1.5rem] bg-zinc-200" />
              <div className="flex flex-col gap-4">
                <div className="h-[5.5rem] w-full animate-pulse rounded-[1.5rem] bg-zinc-200" />
                <div className="h-[5.5rem] w-full animate-pulse rounded-[1.5rem] bg-zinc-200" />
              </div>
            </div>
          ) : (
            <>
              {(chat.seller_id === userDoc?.uid || isLoggedInAsSupport) && (
                <Link
                  className="duration-50 text-zinc-black flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
                  target="_blank"
                  href={`/product/${product.slug}`}
                >
                  View Listing
                </Link>
              )}

              {!product.out_of_stock && chat.buyer_id === userDoc?.uid && (
                <BuyAddOfferButtons
                  product={product}
                  seller={product.seller}
                  selectedVariations={null}
                  onAddToCartError={() => {}}
                />
              )}
            </>
          )}
        </div>
      </motion.div>
    );
  }
}

const SidebarProductImage = ({ product }: { product: ProductDocument }) => {
  // Integration listings ship stock photos framed on a white backdrop, so they
  // have to be contained at full size instead of cropped to fill the square
  const isStockPhoto = !!product.integration_info;

  if (!product.thumbnail) {
    return <div className="aspect-square w-full rounded-2xl bg-zinc-100" />;
  }

  return (
    <Link href={`/product/${product.slug}`} target="_blank">
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl transition-opacity hover:opacity-90',
          isStockPhoto && 'bg-[#f5f5f5]'
        )}
      >
        <SafeImage
          src={product.thumbnail}
          key={product.thumbnail}
          alt={product.title}
          width={400}
          height={400}
          className={cn(
            'aspect-square w-full',
            isStockPhoto ? 'object-contain' : 'object-cover'
          )}
        />

        {product.out_of_stock && <SoldOverlay variant="sold" />}
      </div>
    </Link>
  );
};

const ProductReferences = ({ product }: { product: ProductDocument }) => {
  const { showSuccessToast } = useToastContext();

  const references = [
    { label: 'SKU', value: product.sku },
    { label: 'MPN', value: product.mpn?.[0] },
  ].filter((reference): reference is { label: string; value: string } =>
    Boolean(reference.value)
  );

  if (!references.length) return null;

  const copyReference = (label: string, value: string) => {
    void navigator.clipboard.writeText(value);
    showSuccessToast(`${label} copied to clipboard`);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {references.map(({ label, value }) => (
        <button
          key={label}
          type="button"
          title={`Copy ${label}`}
          onClick={() => copyReference(label, value)}
          className="duration-50 flex max-w-full items-center gap-2 rounded-lg bg-zinc-100 px-3 py-1 text-[1.4rem] font-medium text-zinc-500 transition-colors ease-linear hover:bg-zinc-200 hover:text-zinc-700"
        >
          <span className="truncate">
            {label}: {value}
          </span>

          <CopyIcon className="h-[1.6rem] w-[1.6rem] shrink-0" />
        </button>
      ))}
    </div>
  );
};

export const ProductCategoryDetails = ({
  product,
  className,
  offer,
}: {
  product: ProductDocument;
  className?: string;
  offer?: OfferDocument;
}) => {
  const hasSize = product.size && (product.size.letter || product.size.number);
  const hasVariations = !!product.variations?.length;

  if (!hasSize && !hasVariations) {
    return null;
  }

  const selectedVariations = {
    size: offer?.variation?.letter || offer?.variation?.number,
    color: offer?.variation?.color,
  };

  // Letter and number sizes describe the same item, so they read as one value
  const sizeLabel = product.has_variations
    ? null
    : [product.size?.letter, product.size?.number].filter(Boolean).join(' / ');

  return (
    <div className="flex w-full flex-col gap-4">
      <div className={cn('flex flex-row gap-2', className)}>
        {!!sizeLabel && (
          <span className="line-clamp-1 text-[1.8rem] font-medium text-zinc-500">
            Size: {sizeLabel}
          </span>
        )}

        {!offer && hasVariations && (product.variations?.length ?? 0) > 1 && (
          <span className="line-clamp-1 text-[1.8rem] font-medium text-zinc-500">
            Multiple sizes
          </span>
        )}
      </div>

      {hasVariations && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedVariations.color && (
            <span className="rounded-full bg-zinc-100 px-4 py-1 text-[1.6rem] font-medium text-zinc-600">
              Selected Color: {selectedVariations.color}
            </span>
          )}

          {selectedVariations.size && (
            <span className="rounded-full bg-zinc-100 px-4 py-1 text-[1.6rem] font-medium text-zinc-600">
              Selected Size: {selectedVariations.size}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
